import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import css from './ChipRail.module.css';

/**
 * ChipRail — a fixed-height row of chips that scrolls sideways (P25009-453).
 *
 * Every group of small repeating items on the search page (active filter
 * chips, saved cars, saved searches, size chips, seller pills) renders inside
 * one of these instead of wrapping onto new lines, so the area above the
 * first listing stays the same height no matter how many items it holds
 * (BR-1). The Däck/Fälg/Hjul segmented control is deliberately NOT a rail.
 *
 * - The row height is fixed (44px default, override via `railHeight`) and
 *   never grows with content.
 * - Chips bleed to the screen edge on mobile and a soft fade marks overflow
 *   at the right edge, so the partially cut chip stays visible as the "there
 *   is more" affordance (BR-2).
 * - The scrollbar is hidden but the rail keeps a tab stop, so it stays
 *   keyboard-scrollable; on fine-pointer devices left/right chevrons scroll
 *   ~80% of the visible width, the left one hidden at the start (BR-3).
 * - An empty rail renders nothing at all — no empty strip (BR-4). Vertical
 *   page scroll passes through (touch-action: pan-x pan-y).
 */
const ChipRail = props => {
  const { label, className, railClassName, railHeight, ariaLabel, children } = props;

  const railRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateOverflow = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    updateOverflow();
    el.addEventListener('scroll', updateOverflow, { passive: true });
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateOverflow);
      resizeObserver.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [updateOverflow, children]);

  /* toArray drops null/undefined/booleans, so a rail whose every item
   * rendered to nothing disappears entirely (BR-4). */
  const items = React.Children.toArray(children);
  if (items.length === 0) return null;

  const scrollByStep = direction => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  /* Inline style, not a CSS custom property — postcss-apply in this build
   * silently drops custom properties declared outside :root. */
  const railStyle = railHeight ? { height: `${railHeight}px` } : undefined;

  return (
    <div className={classNames(css.root, className)}>
      {label ? <div className={css.label}>{label}</div> : null}
      <div className={css.railWrap}>
        {canScrollLeft ? (
          <button
            type="button"
            className={classNames(css.chevron, css.chevronLeft)}
            onClick={() => scrollByStep(-1)}
            aria-label="Bläddra bakåt"
            tabIndex={-1}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        ) : null}
        <div
          className={classNames(css.rail, railClassName)}
          style={railStyle}
          ref={railRef}
          role="group"
          aria-label={ariaLabel || label || 'Bläddringsbar rad'}
          tabIndex={0}
        >
          {items}
        </div>
        {canScrollRight ? (
          <>
            <div className={css.fadeRight} aria-hidden="true" />
            <button
              type="button"
              className={classNames(css.chevron, css.chevronRight)}
              onClick={() => scrollByStep(1)}
              aria-label="Bläddra framåt"
              tabIndex={-1}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ChipRail;
